import template from './sw-extension-store-detail.html.twig';
import {
    getLegacyServicesExtensionRoute,
    isLegacyServicesExtension,
} from 'SwagExtensionStore/util/legacy-services-extension';
import './sw-extension-store-detail.scss';

const { Utils } = Shopware;

/**
 * @private
 */
export default {
    template,

    inject: [
        'extensionStoreService',
        'extensionStoreDataService',
        'shopwareExtensionService',
        'extensionHelperService',
        'cacheApiService',
        'inAppPurchasesService',
    ],

    mixins: ['sw-extension-error'],

    props: {
        id: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            extension: null,
            inAppPurchases: [],
            isLoading: false,
            showBuyModal: false,
            showPermissionsModal: false,
            showAcceptPermissionsModal: false,
            showAccountModal: false,
            showInstallationFailedModal: false,
            showInAppPurchasesListingModal: false,
            isInstalling: false,
            isInstallSuccessful: false,
            permissionsAccepted: false,
            isDescriptionCollapsed: false,
            installationError: null,
            fetchError: false,
            canBeOpened: false,
        };
    },

    computed: {
        suspended() {
            return this.extension === null;
        },

        calculatedPrice() {
            return this.extensionStoreService.getCalculatedPrice(this.recommendedVariant);
        },

        calculatedPriceSnippet() {
            return this.extensionStoreService.getCalculatedPriceSnippet(this.extension.variants);
        },

        extensionMetaData() {
            if (this.suspended) {
                return null;
            }

            return Shopware.Store.get('shopwareExtensions').myExtensions.data.find((extension) => {
                return extension.id === this.extension.id;
            });
        },

        isFree() {
            return this.extensionStoreService.isVariantOfTypeFree(this.recommendedVariant);
        },

        isLicensed() {
            if (!this.extensionMetaData) {
                return false;
            }

            return !!this.extensionMetaData.storeLicense;
        },

        isInstalled() {
            return !!this.extensionMetaData && !!this.extensionMetaData.installedAt;
        },

        isConfigurable() {
            return !!this.extensionMetaData && this.extensionMetaData.configurable;
        },

        images() {
            if (this.suspended) {
                return [];
            }

            return this.extension.images.map((image) => {
                return image.remoteLink;
            });
        },

        extensionCategoryNames() {
            if (this.suspended) {
                return '';
            }

            return this.extension.categories.map((category) => category.details.name).join(', ');
        },

        extensionLanguages() {
            if (this.suspended) {
                return '';
            }

            return this.extension.languages.join(', ');
        },

        isPurchasable() {
            if (this.suspended) {
                return false;
            }

            if (!this.extension.variants.length) {
                return false;
            }

            return !this.isLicensed;
        },

        languageId() {
            return Shopware.Store.get('session').languageId;
        },

        recommendedVariant() {
            return this.extensionStoreService.getRecommendedVariant(this.extension.variants);
        },

        dateFilter() {
            return Shopware.Filter.getByName('date');
        },

        dateFilterOptions() {
            return {
                month: 'numeric',
                year: 'numeric',
            };
        },

        hasActiveDiscount() {
            return this.extensionStoreService.isExtensionDiscounted(this.extension.variants);
        },

        variantClass() {
            return {
                'is--theme': this.extension && this.extension.isTheme,
            };
        },

        orderedBinaries() {
            return Utils.get(this.extension, 'binaries', []).slice().reverse();
        },

        priceClass() {
            return {
                'sw-extension-store-detail__price-discounted': this.hasActiveDiscount,
            };
        },

        description() {
            return Utils.get(this.extension, 'description');
        },

        hasPermissions() {
            return Object.keys(this.extension.permissions).length;
        },

        installationErrorDocumentationLink() {
            return Utils.get(this.installationError, 'meta.documentationLink', null);
        },

        isEnterpriseFeature() {
            if (this.suspended) {
                return false;
            }

            return !!this.extension.addons.find(addon => addon === 'SW6_EnterpriseFeature');
        },

        isLegacyServicesExtension() {
            if (this.suspended) {
                return false;
            }

            return isLegacyServicesExtension(this.extension.name);
        },
    },

    watch: {
        id: {
            immediate: true,
            handler() {
                this.fetchExtension();
            },
        },

        '$route.hash'() {
            this.scrollToElementFromHash();
        },

        suspended() {
            if (!this.suspended) {
                // wait for all child components to be mounted
                this.$nextTick(() => {
                    this.scrollToElementFromHash();
                });
            }
        },

        languageId(newValue) {
            if (newValue !== '') {
                this.fetchExtension();
            }
        },

        description() {
            this.isDescriptionCollapsed = true;
            this.$nextTick(() => {
                this.checkDescriptionCollapsed();
            });
        },
    },

    async created() {
        await this.shopwareExtensionService.updateExtensionData();
        this.canBeOpened = !!this.shopwareExtensionService.getOpenLink(this.extension);
        if (this.extension.inAppFeaturesAvailable) {
            this.fetchInAppPurchases();
        }
    },

    methods: {
        async fetchExtension() {
            this.isLoading = true;

            if (this.languageId === '') {
                return;
            }

            try {
                this.fetchError = false;
                this.extension = await this.extensionStoreDataService.getDetail(
                    this.id,
                    { ...Shopware.Context.api, languageId: this.languageId },
                );
            } catch (error) {
                const errorData = error.response.data.errors[0];

                if (errorData.code === 'FRAMEWORK__STORE_ERROR' && errorData.title === 'Extension unknown') {
                    this.fetchError = errorData;


                    const docLink = this.$t('sw-extension.errors.messageToTheShopwareDocumentation', errorData.meta);

                    this.createNotificationError({
                        title: errorData.title,
                        message: `${errorData.detail} <br> ${docLink}`,
                        autoClose: false,
                    });

                    return;
                }

                this.showExtensionErrors(error);
            } finally {
                this.isLoading = false;
            }
        },

        async fetchExtensionAndScrollToRatings() {
            await this.fetchExtension();
            this.$router.push({ hash: '#ratings-card' });
        },

        scrollToElementFromHash() {
            if (!this.$route.hash) {
                return;
            }

            const elementWithHashId = this.$el.querySelector(this.$route.hash);
            const pageBody = document.querySelector('div.sw-meteor-page__body');
            const header = document.querySelector('header.sw-meteor-page__head-area');

            const topOfElement = elementWithHashId.offsetTop - header.getBoundingClientRect().height;

            pageBody.scroll({ top: topOfElement, behavior: 'smooth' });

            // clear hash so user can click link again
            this.$router.push({ ...this.route, hash: null });
        },

        async onClickAddExtension() {
            await this.shopwareExtensionService.checkLogin();

            if (!Shopware.Store.get('shopwareExtensions').userInfo) {
                this.openAccountModal();
                return;
            }

            this.openBuyModal();
        },

        renderPrice(price) {
            return Utils.format.currency(price, 'EUR');
        },

        onLoginSuccess() {
            this.closeAccountModal();
            this.openBuyModal();
        },

        openBuyModal() {
            this.showBuyModal = true;
        },

        closeBuyModal() {
            this.showBuyModal = false;
        },

        openAccountModal() {
            this.showAccountModal = true;
        },

        closeAccountModal() {
            this.showAccountModal = false;
        },

        openPermissionsModal() {
            this.showPermissionsModal = true;
        },

        closePermissionsModal() {
            this.showPermissionsModal = false;
        },

        openAcceptPermissionsModal() {
            this.showAcceptPermissionsModal = true;
        },

        closeAcceptPermissionsModal() {
            this.showAcceptPermissionsModal = false;
        },

        openInAppPurchasesListingModal() {
            this.showInAppPurchasesListingModal = true;
        },

        closeInAppPurchasesListingModal() {
            this.showInAppPurchasesListingModal = false;
        },

        async closePermissionsModalAndInstallExtension() {
            this.permissionsAccepted = true;
            this.closeAcceptPermissionsModal();
            await this.installExtension();
        },

        async handleInstallWithPermissionsModal() {
            if (this.isLegacyServicesExtension) {
                this.openLegacyServicesExtension();

                return;
            }

            if (Object.keys(this.extension.permissions).length) {
                this.openAcceptPermissionsModal();

                return;
            }

            this.permissionsAccepted = true;
            await this.installExtension();
        },

        async installExtension() {
            if (this.isLegacyServicesExtension) {
                this.openLegacyServicesExtension();

                return;
            }

            this.isInstalling = true;

            try {
                await this.extensionHelperService.downloadAndActivateExtension(this.extension.name, this.extension.type);

                if (this.extension.type === 'plugin') {
                    await this.cacheApiService.clear();
                }

                this.isInstallSuccessful = true;

                window.location.reload();
            } catch (error) {
                this.showExtensionErrors(error);

                if (Utils.get(error, 'response.data.errors[0]', null)) {
                    this.installationError = error.response.data.errors[0];
                }

                this.openInstallationFailedModal();
            } finally {
                this.isInstalling = false;
            }
        },

        async finishedInstall() {
            await this.shopwareExtensionService.updateExtensionData();
            this.isInstallSuccessful = false;
        },

        async openExtension() {
            const openLink = await this.shopwareExtensionService.getOpenLink(this.extension);

            if (openLink) {
                this.$router.push(openLink);
            }
        },

        openConfiguration() {
            this.$router.push({ name: 'sw.extension.config', params: { namespace: this.extension.name } });
        },

        openListingPage() {
            if (this.extension && this.extension.isTheme) {
                this.$router.push({ name: 'sw.extension.my-extensions.listing.theme' });
                return;
            }

            this.$router.push({ name: 'sw.extension.my-extensions.listing.app' });
        },

        openLegacyServicesExtension() {
            const route = getLegacyServicesExtensionRoute(this.extension?.name);

            if (route) {
                this.$router.push(route);
            }
        },

        checkDescriptionCollapsed() {
            const description = this.$el.querySelector('.sw-extension-store-detail__description');

            if (description && description.scrollHeight <= 300) {
                this.expandDescription();
            }
        },

        expandDescription() {
            this.isDescriptionCollapsed = false;
        },

        openInstallationFailedModal() {
            this.showInstallationFailedModal = true;
        },

        closeInstallationFailedModal() {
            this.showInstallationFailedModal = false;
        },

        async fetchInAppPurchases() {
            this.inAppPurchases = await this.inAppPurchasesService.getAvailablePurchases(this.extension.name);
        },
    },
};
